'use client';

/**
 * Initializes Jarvis chat. Opens via document click listener on #jarvis-launcher.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAdobeIMS } from '@frameio/connections/AdobeIMS/useAdobeIMS';
import { intl } from '@frameio/i18n';
import { useMeCachedQuery } from '@frameio/shared/hooks';
import { jarvisClient } from './JarvisClient';
import {
  buildJarvisContext,
  getJarvisConfig,
  imsAccessTokenFromResponse,
} from './jarvisInitHelpers';
import { setJarvisSupportLauncherAwaitingAdobeModalSignIn } from './jarvisSupportLauncherAwaitingAdobeModalSignIn';
import { loadJarvisScripts } from './loadJarvis';
import { useJarvisSupportLauncherState } from './useJarvisSupportLauncherState';

export function useJarvis(): void {
  const { jarvisIntegrationEnabled } = useJarvisSupportLauncherState();
  const launcherSignInRef = useRef<(() => void) | null>(null);
  const openChatAfterModalSignInRef = useRef(false);
  const {
    getAccessToken,
    initialized,
    hasCompletedModalSignIn,
    signIn: imsSignIn,
  } = useAdobeIMS({ modalSignIn: true });

  const imsTokenRef = useRef<string | null>(null);
  const getToken = useCallback(() => imsTokenRef.current ?? undefined, []);

  const { data: meData } = useMeCachedQuery();
  const me = meData?.me;
  const meId = me?.id;
  const meEmail = me?.email;
  const meName = me?.name;
  const locale = intl().locale || 'en-US';

  useEffect(() => {
    if (!jarvisIntegrationEnabled) {
      return;
    }
    loadJarvisScripts();
    jarvisClient.ensureSupportLauncherFallback();
  }, [jarvisIntegrationEnabled]);

  useEffect(() => {
    const onCapture = (e: MouseEvent) => {
      const run = launcherSignInRef.current;
      const { target } = e;
      if (
        !run ||
        !(target instanceof Element) ||
        !target.closest('#jarvis-launcher')
      )
        return;
      e.preventDefault();
      e.stopPropagation();
      run();
    };
    document.addEventListener('click', onCapture, { capture: true });
    return () =>
      document.removeEventListener('click', onCapture, { capture: true });
  }, []);

  useEffect(() => {
    if (!jarvisIntegrationEnabled) {
      launcherSignInRef.current = null;
      openChatAfterModalSignInRef.current = false;
      imsTokenRef.current = null;
      jarvisClient.setSupportLauncherMode('inactive');
      setJarvisSupportLauncherAwaitingAdobeModalSignIn(false);
      return undefined;
    }

    if (!initialized || !meId) {
      launcherSignInRef.current = null;
      openChatAfterModalSignInRef.current = false;
      imsTokenRef.current = null;
      jarvisClient.setSupportLauncherMode('inactive');
      setJarvisSupportLauncherAwaitingAdobeModalSignIn(false);
      return undefined;
    }

    let cancelled = false;
    jarvisClient.setSupportLauncherMode('inactive');
    launcherSignInRef.current = null;
    setJarvisSupportLauncherAwaitingAdobeModalSignIn(false);

    (async () => {
      try {
        const next = imsAccessTokenFromResponse(await getAccessToken());
        if (cancelled) return;

        if (!next) {
          imsTokenRef.current = null;
          if (!hasCompletedModalSignIn) {
            launcherSignInRef.current = () => {
              openChatAfterModalSignInRef.current = true;
              imsSignIn({
                dc: true,
                eu: Boolean(meEmail),
                puser: meEmail ?? undefined,
              }).catch(() => undefined);
            };
            setJarvisSupportLauncherAwaitingAdobeModalSignIn(true);
            return;
          }
          launcherSignInRef.current = null;
          openChatAfterModalSignInRef.current = false;
          jarvisClient.setSupportLauncherMode('adobe_help');
          return;
        }

        launcherSignInRef.current = null;
        imsTokenRef.current = next;

        const onReady = () => {
          jarvisClient.hideDefaultCTA();
          if (openChatAfterModalSignInRef.current) {
            openChatAfterModalSignInRef.current = false;
            jarvisClient.openWindow({
              sourceType: 'button',
              sourceText: 'Contact Support',
            });
          }
        };

        const { env } = getJarvisConfig();
        const context = buildJarvisContext({
          token: next,
          me: { id: meId, email: meEmail, name: meName },
          locale,
          env,
          onReady,
          getToken,
        });

        const result = await jarvisClient.init(context, { env });
        if (cancelled) return;

        if (
          result?.status !== 'success' &&
          result?.type !== 'init_already_done'
        ) {
          openChatAfterModalSignInRef.current = false;
          jarvisClient.setSupportLauncherMode('adobe_help');
          // eslint-disable-next-line no-console
          console.error('[Jarvis] Init failed:', result?.type);
        } else {
          jarvisClient.setSupportLauncherMode('jarvis');
          if (result?.type === 'init_already_done') {
            onReady();
          }
        }
      } catch (error) {
        if (!cancelled) {
          openChatAfterModalSignInRef.current = false;
          jarvisClient.setSupportLauncherMode('adobe_help');
          setJarvisSupportLauncherAwaitingAdobeModalSignIn(false);
          // eslint-disable-next-line no-console
          console.error('[Jarvis] IMS token or init error:', error);
        }
      }
    })();

    return () => {
      cancelled = true;
      launcherSignInRef.current = null;
      jarvisClient.setSupportLauncherMode('inactive');
      setJarvisSupportLauncherAwaitingAdobeModalSignIn(false);
    };
  }, [
    jarvisIntegrationEnabled,
    hasCompletedModalSignIn,
    initialized,
    meId,
    meEmail,
    meName,
    locale,
    getAccessToken,
    getToken,
    imsSignIn,
  ]);
}
